import React from "react";
import { Button, FormProps, notification } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useResetPasswordMutation } from "store/api";
import InputField from "components/Common/InputField";
import { ErrorAlert, Logo } from "components/Common";
import * as Yup from "yup";
import { Formik } from "formik";

type FieldType = {
  newPassword?: string;
  confirmPassword?: string;
};

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      await resetPassword({
        token,
        password: values.newPassword,
      }).unwrap();
      navigate("/");
      notification.success({ message: "Password reset successfully" });
    } catch (error: any) {
      setError(
        error?.data?.error || "Failed to reset password. Please try again.",
      );
    }
  };

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-full h-full flex flex-col justify-center">
        <Logo />
        <h1 className="text-2xl font-bold mb-1 text-center">Reset Password</h1>
        <p className="mb-4 text-center">
          Enter your email to reset your password
        </p>
        <Formik
          validateOnBlur
          initialValues={{
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={Yup.object().shape({
            newPassword: Yup.string()
              .required("Password is required")
              .min(14, "Password must be at least 14 characters")
              .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
              ),
            confirmPassword: Yup.string()
              .required("Confirm password is required")
              .oneOf([Yup.ref("newPassword"), ""], "Passwords don't match"),
          })}
          onSubmit={async (values) => {
            await onFinish(values);
          }}
        >
          {({ handleSubmit, isValid, dirty }) => (
            <form onSubmit={handleSubmit} className="space-y-3">
              <ErrorAlert type="error" message={error} setMessage={setError} />

              <InputField
                label="Password"
                name="newPassword"
                type="password"
                placeholder="Enter New password"
              />
              <InputField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                placeholder="Confirm password"
              />

              <Button
                type="primary"
                htmlType="submit"
                className="w-full h-10"
                loading={isLoading}
                disabled={!isValid || !dirty || isLoading}
              >
                Reset Password
              </Button>
            </form>
          )}
        </Formik>
        <div className="flex items-center justify-end p-3 gap-2">
          <Link to="/" className="text-blue-500">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
